import { useEffect, useState } from "react";

import { t } from "../i18n";
import { LockedField } from "./fields";
import type { AnsweredField, Locale } from "../types";

/**
 * Udzielone odpowiedzi. Przy trzydziestu pytaniach pełna kaskada jest nie do
 * przewinięcia, więc widoczne zostają dwie ostatnie, a reszta chowa się pod
 * jednym przełącznikiem. Nic nie znika — zmienia się tylko domyślny stan.
 */
const VISIBLE = 2;

export function Answers({
  fields,
  locale,
  busy,
  autofilled,
  onClear,
  recordId,
}: {
  fields: AnsweredField[];
  locale: Locale;
  busy: boolean;
  /** Klucze wypełnione przez ostatnią akcję automatycznie (KYC, OCR, geokodowanie). */
  autofilled: string[];
  onClear: (key: string) => void;
  /** Sprawa, do której należą podpisane dokumenty — do pobrania PDF-u. */
  recordId: string;
}) {
  const [expanded, setExpanded] = useState(false);

  // Nowa odpowiedź zwija listę z powrotem — inaczej po rozwinięciu rośnie w nieskończoność.
  useEffect(() => setExpanded(false), [fields.length]);

  if (fields.length === 0) return null;

  // Cała wartość skanu dokumentu polega na tym, że widać, co się wypełniło samo.
  // Te pola zostają na wierzchu nawet wtedy, gdy reszta historii jest zwinięta.
  const fresh = new Set(autofilled);
  const recent = fields.slice(-VISIBLE).map((f) => f.key);
  const keep = new Set([...fresh, ...recent]);

  const shown = expanded ? fields : fields.filter((f) => keep.has(f.key));
  const hiddenCount = fields.length - shown.length;

  return (
    <div className="answers">
      {fresh.size > 0 && (
        <p className="autofill" role="status">
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="m5 12.5 4.5 4.5L19 7.5" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t("autofilled", locale).replace("{n}", String(fresh.size))}
        </p>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          className="answers__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((previous) => !previous)}
        >
          <b>{t("yourAnswers", locale)}</b>
          <span>
            {expanded ? t("hideAnswers", locale) : `+${hiddenCount} ${t("moreAnswers", locale)}`}
          </span>
          <span className="answers__rule" aria-hidden="true" />
          <svg
            className={`answers__chevron${expanded ? " answers__chevron--open" : ""}`}
            width="18"
            height="18"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="m6 9 6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {shown.map((field) => (
        <LockedField
          key={field.key}
          field={field}
          locale={locale}
          busy={busy}
          // Krótkie potwierdzenie dostaje tylko odpowiedź, która właśnie
          // usiadła (ostatnia albo świeżo wypełniona automatycznie) —
          // reszta listy zgodnie z DESIGN.md nie animuje się nigdy.
          fresh={fresh.has(field.key) || field.key === fields[fields.length - 1]?.key}
          onClear={() => onClear(field.key)}
          recordId={field.type === "simple_sign" ? recordId : undefined}
        />
      ))}
    </div>
  );
}
